import { Injectable } from '@angular/core';
import { MapService } from 'src/app/services/map.service';
import olMap from 'ol/Map';
import { unByKey } from 'ol/Observable';
import { SearchItem } from 'src/app/models/searchItem';
import { SearchService } from 'src/app/services/search.service';
import { CalculationsService } from 'src/app/services/calculations.service';
import Overlay from 'ol/Overlay';
import { Coordinate } from 'ol/coordinate';
import * as olProj from 'ol/proj';
import { Feature } from 'ol';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { CoordenateService } from 'src/app/services/coordenate.service';
import { PointsService } from 'src/app/services/points.service';
import { GlobalSelectionControlService } from './global-selection-control.service';
import { UiBlockService } from 'src/app/services/ui-block.service';
import { EventListenersOnMap } from 'src/app/utils/eventListenersOnMap';


@Injectable({
  providedIn: 'root',
})
export class TerrainHeightService {
  active: boolean = false;

  map: olMap;
  pointerMoveListener;
  pointerClickListener;

  helpTooltipElement;
  helpTooltip;

  layer;
  source = new VectorSource();

  measureTooltip: Overlay;
  measureTooltipElement;

  private static readonly NO_TERRAIN_DATA_MESSAGE = 'No terrain data available';
  static readonly EVENT_ON_MAP_LABEL: string = 'Terrain Height';
  static readonly EVENT_ON_MAP_LABEL2: string = 'pm';

  constructor(
    private mapService: MapService,
    private searchService: SearchService,
    private calculationService: CalculationsService,
    private rcsControl: CoordenateService,
    private pointsService: PointsService,
    private uiBlock: UiBlockService
  ) {
    this.map = mapService.getMap('map');
    this.layer = new VectorLayer({
      source: this.source,
    });
    this.map.addLayer(this.layer);

  }

  activeTerrainHeight() {
    this.active = !this.active;

    if (this.active) {
      this.createPointerMoveEvent();
      this.createClickEvent();

      this.createHelpTooltip();
      this.createMeasureTooltip();
    } else {
      this.clearListeners();
    }
  }

  createPointerMoveEvent() {
    EventListenersOnMap.addEventListener(
      TerrainHeightService.EVENT_ON_MAP_LABEL2,
      'pointermove',
      function (evt) {
        if (evt.dragging) {
          return;
        }

        let helpMsg = 'Click to get height';

        this.helpTooltipElement.innerHTML = helpMsg;
        this.helpTooltip.setPosition(evt.coordinate);
        this.helpTooltipElement.classList.remove('hidden');
      }.bind(this),
      false
    );
  }

  createClickEvent() {
    EventListenersOnMap.addEventListener(
      TerrainHeightService.EVENT_ON_MAP_LABEL,
      'click',
      function (evt) {
        if (evt.dragging) {
          return;
        }

        this.calculateHeight(evt.coordinate);
      }.bind(this),
      true
    );
  }

  createHelpTooltip() {
    if (this.helpTooltipElement) {
      this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
    }
    this.helpTooltipElement = document.createElement('div');
    this.helpTooltipElement.setAttribute('id', 'helpToolTip');
    this.helpTooltipElement.className = 'ol-tooltip ';
    this.helpTooltip = new Overlay({
      element: this.helpTooltipElement,
      offset: [15, 0],
    });
    this.map.addOverlay(this.helpTooltip);
  }

  calculateHeight(coord) {
    if (this.active) {
      let target: Coordinate = [coord[0], coord[1]];
      let coordinate: Coordinate = olProj.toLonLat(
        target,
        this.rcsControl.activeRCS.isactive
          ? this.rcsControl.activeRCS.epsg
          : 'EPSG:3857'
      );
      this.uiBlock.startBlock('Getting Terrain Height...');
      this.pointsService.calculateTerrainHeightAtPoint(coordinate).subscribe({
        next: (terrainInformation) => {
          if (terrainInformation == null) {
            this.addMeasuteTooltip(
              coord,
              TerrainHeightService.NO_TERRAIN_DATA_MESSAGE
            );
            return;
          }
          document.getElementsByClassName(
            'terrain-height_container'
          )[0].innerHTML =
            'Terrain height: ' +
            terrainInformation['terrainHeigth'] +
            ' [m] <br>' +
            'Terrain type: ' +
            terrainInformation['terrainSources'];

          this.addMeasuteTooltip(coord, terrainInformation['terrainHeigth']);
        },
        error: () => {
          this.addMeasuteTooltip(
            coord,
            TerrainHeightService.NO_TERRAIN_DATA_MESSAGE
          );
          this.uiBlock.stopBlock();
        },
        complete: () => {
          this.uiBlock.stopBlock();
        },
      });
    }
  }

  createMeasureTooltip() {
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-height';
  }

  addMeasuteTooltip(coordinate, height) {
    let info = document.createElement('div');
    info.innerHTML = height;
    if (height != TerrainHeightService.NO_TERRAIN_DATA_MESSAGE) {
      info.innerHTML += ' [m]';
    }
    info.className = 'ol-tooltip ol-tooltip-static';

    this.measureTooltipElement.appendChild(info);

    this.measureTooltip = new Overlay({
      element: info,
      offset: [0, -28],
      stopEvent: false,
      insertFirst: false,
      position: coordinate,
    });

    this.map.addOverlay(this.measureTooltip);

    this.source.addFeature(
      new Feature({
        geometry: new Point(coordinate),
      })
    );
  }

  removeElementsByClass(className) {
    const elements = document.getElementsByClassName(className);
    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0]);
    }
    this.active = false;
  }

  clearListeners() {
    // Clear overlays data
    this.removeElementsByClass('ol-tooltip-static');

    // Clear pointer tooltip
    if (this.helpTooltipElement != null) {
      document.getElementById('helpToolTip').remove();
      this.helpTooltipElement = null;
    }

    // Clear points
    this.layer.getSource().clear();
    this.map.removeLayer(this.layer);
  }
}
